/**
 * @voltxampere/shared — User error leaf classes
 *
 * Covers SPECS §5.2 rows 1–6 (the `E_VALIDATION_*` family). Every
 * leaf pins a single code from {@link VXAErrorCode}, fixes its
 * severity to match the spec table, and declares whether the
 * condition is recoverable (all validation errors are — the user
 * can correct the underlying data).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { UserError } from "./categories.js";
export class ValidationSchemaError extends UserError {
    code = "E_VALIDATION_SCHEMA";
    recoverable = true;
    defaultSeverity() {
        return "error";
    }
}
export class ValidationNodeMissingError extends UserError {
    code = "E_VALIDATION_NODE_MISSING";
    recoverable = true;
    defaultSeverity() {
        return "error";
    }
}
export class ValidationNetMissingError extends UserError {
    code = "E_VALIDATION_NET_MISSING";
    recoverable = true;
    defaultSeverity() {
        return "error";
    }
}
export class ValidationParamError extends UserError {
    code = "E_VALIDATION_PARAM";
    recoverable = true;
    defaultSeverity() {
        return "error";
    }
}
/**
 * Sprint 55 — stamp-specific parameter validation error. Distinct
 * from {@link ValidationParamError} (Zod schema layer) because stamp
 * params surface in user-facing Inspector flows with structured
 * context that the Recovery Center routes into a typed suggestion
 * lookup.
 *
 * Recommended `meta` shape (additive — not enforced at the type
 * level, so legacy throw sites still work):
 *
 *   {
 *     stampType: string;       // "resistor" | "bjt-npn" | "triac" | ...
 *     parameter: string;       // "resistance" | "Bf" | "vGateTurnOff" | ...
 *     received: unknown;       // observed value
 *     expected: string;        // "> 0" | "between 0.1 and 100" | ...
 *     componentId?: string;    // when known, surfaces in UI hint
 *   }
 */
export class StampParameterError extends UserError {
    code = "E_VALIDATION_STAMP_PARAM";
    recoverable = true;
    defaultSeverity() {
        return "error";
    }
}
export class ValidationDuplicateIdError extends UserError {
    code = "E_VALIDATION_DUPLICATE_ID";
    recoverable = true;
    defaultSeverity() {
        return "error";
    }
}
export class ValidationGroundMissingError extends UserError {
    code = "E_VALIDATION_GROUND_MISSING";
    recoverable = true;
    defaultSeverity() {
        return "error";
    }
}
/**
 * A circuit references a component `type` that is not registered in
 * `@voltxampere/components`. Per SPECS §1.3 invariants, every
 * `ComponentInstance.type` must match a registered definition, so an
 * unknown type is a schema-level failure and pins to
 * `E_VALIDATION_SCHEMA` (the catch-all code for schema invariants that
 * don't have their own dedicated entry in SPECS §5.2).
 *
 * The class exists separately from {@link ValidationSchemaError}
 * because the recovery UX is different — users can correct the
 * component type from a picker; they cannot in general repair a
 * corrupt top-level schema by hand.
 */
export class UnknownComponentTypeError extends UserError {
    code = "E_VALIDATION_SCHEMA";
    recoverable = true;
    defaultSeverity() {
        return "error";
    }
}
//# sourceMappingURL=user-errors.js.map